import React from 'react';
import { Box, Typography, Stack , Button} from '@mui/material';
import { CrownIcon } from './VipIcons'; // Assuming you have this, or remove if not
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import * as Constant from "../../features/constant";
import { useGetDepositDetailsQuery } from '../../features/api/transactionApiSlice';
import { getVipRankLabel, findEligibleUpgrade } from "../../utils/vipRank";
import { useManualUpgradeVipMutation } from "../../features/api/userApiSlice";

const COLORS = {
  slate900: '#0f172a',
  slate800: '#1e293b',
  slate400: '#94a3b8',
  slate300: '#cbd5e1',
  amber500: '#f59e0b',
  amber600: '#d97706',
  white: '#ffffff',
};


const VipProgress = ({ ranks, currentDeposit, userRank }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sortedRanks = [...ranks].sort((a, b) => (a.accumulatedDeposit || 0) - (b.accumulatedDeposit || 0));


  let currentRankIndex = 0;

  if (userRank) {
    // A. PRIORITY: Use API Rank (e.g. "VIP5")
    const rankNum = parseInt(userRank.replace(/VIP/i, '') || "0");
    const foundIndex = sortedRanks.findIndex(r => parseInt(r.vipRank) === rankNum);
    
    // If found, use it. If -1 (not found), default to 0.
    currentRankIndex = foundIndex !== -1 ? foundIndex : 0;
  } else {
    // B. FALLBACK: Calculate based on money if userRank prop is missing
    for (let i = 0; i < sortedRanks.length; i++) {
      const req = sortedRanks[i].accumulatedDeposit || 0;
      if (currentDeposit >= req) {
        currentRankIndex = i;
      } else {
        break; 
      }
    }
  }

    const {
      data: depositDetails,
      isLoading: isDepositDetailsLoading,
      isError: isDepositDetailsError,
      isSuccess: isDepositDetailsSuccess,
      error: depositDetailsError,
    } = useGetDepositDetailsQuery();

  const currentRank = sortedRanks[currentRankIndex];
  const nextRank = sortedRanks[currentRankIndex + 1];

  // 4. Calculate Progress
  let progressPercent = 0;
  let nextRequirement = 0;

  if (!nextRank) {
    progressPercent = 100; // Max Level
  } else {
    nextRequirement = nextRank.accumulatedDeposit;
    
    // Logic: Calculate absolute progress towards the NEXT goal.
    // This handles manual upgrades nicely (e.g. You are VIP5 but deposit is $0 -> Progress is 0%)
    if (nextRequirement > 0) {
      progressPercent = Math.min(100, Math.max(0, (currentDeposit / nextRequirement) * 100));
    } else {
      progressPercent = 100;
    }
  }

  const formatMoney = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: process.env.REACT_APP_CURRENCY_CODE, maximumFractionDigits: 0 }).format(val);
  const amountLeft = nextRank ? Math.max(0, nextRequirement - currentDeposit) : 0;
  const currency = process.env.REACT_APP_CURRENCY_CODE || "USD";

  // Manual upgrade path — swap Deposit Now for Upgrade when eligible.
  const eligibleUpgrade = findEligibleUpgrade(userRank, currentDeposit, sortedRanks);
  const [triggerUpgrade, { isLoading: isUpgrading }] = useManualUpgradeVipMutation();

  const onUpgradeClick = async () => {
    try {
      const res = await triggerUpgrade().unwrap();
      if (res?.success) {
        const newRankLabel = getVipRankLabel({
          vipRank: parseInt(String(res?.result?.newRank || "").replace(/\D/g, ""), 10),
          customDisplayName: eligibleUpgrade?.customDisplayName,
        });
        Swal.fire({
          icon: "success",
          title: `Upgraded to ${newRankLabel}`,
          text: `${res?.result?.upgradeAmount || 0} ${currency} bonus credited`,
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: "Upgrade failed", text: res?.message || "Please try again" });
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Upgrade failed", text: e?.data?.message || e?.message || "Please try again" });
    }
  };

return (
    <Box
      sx={{
        backgroundColor: COLORS.slate900,
        border: `1px solid ${COLORS.slate800}`,
        borderRadius: '16px', 
        p: { xs: 3, md: 4 }, 
        mb: 5,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '100%', background: 'radial-gradient(ellipse at center, rgba(245, 158, 11, 0.15), transparent 70%)', zIndex: 0 }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Stack direction="row" spacing={2} alignItems="center">
              {currentRank?.logo ? (
                <Box 
                  component="img"
                  src={currentRank.logo}
                  alt="Rank Logo"
                  sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 }, objectFit: 'contain' }}
                />
              ) : (
                <Box sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%' }} /> 
              )}

              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: COLORS.white, lineHeight: 1.2 }}>
                  {getVipRankLabel(currentRank)}
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.slate400, display: 'block' }}>
                  {t("vipnew.currentstatus")}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Right Side Header: Button + Next Rank Info */}
          <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            
            {/* Upgrade button when eligible + manual gate, else Deposit Now */}
            {eligibleUpgrade ? (
              <Button
                variant="contained"
                size="small"
                onClick={onUpgradeClick}
                disabled={isUpgrading}
                sx={{
                  backgroundColor: '#4ade80',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#22c55e' },
                }}
              >
                {t("vipnew.upgradenow", {
                  rank: getVipRankLabel(eligibleUpgrade),
                  defaultValue: `Upgrade to ${getVipRankLabel(eligibleUpgrade)}`,
                })}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(Constant.getPath("/deposit"), { state: { depositDetails: depositDetails?.result } })}
                sx={{
                  backgroundColor: COLORS.amber500,
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: COLORS.amber600 },
                }}
              >
                {t("vipnew.deposit_now") || "Deposit Now"}
              </Button>
            )}

            {/* Desktop Next Rank Info (stays hidden on mobile) */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
               {nextRank ? (
                 <>
                   <Typography variant="body1" sx={{ color: COLORS.slate300, fontWeight: 'bold' }}>
                     {t("vipnew.next")}: VIP {nextRank.vipRank}
                   </Typography>
                   <Typography variant="caption" sx={{ color: COLORS.amber500 }}>
                     {formatMoney(amountLeft)} {t("vipnew.toupgrade")}
                   </Typography>
                 </>
               ) : (
                 <Typography variant="h6" sx={{ color: COLORS.amber500 }}>{t("vipnew.maxrank")}</Typography>
               )}
            </Box>
          </Box>
        </Stack>

        {/* Mobile Only Section */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'space-between', mb: 1 }}>
           <Typography variant="caption" sx={{ color: COLORS.slate400 }}>{t("vipnew.progress")}</Typography>
           {nextRank && (
              <Typography variant="caption" sx={{ color: COLORS.amber500 }}>
                {formatMoney(amountLeft)} {t("vipnew.left")}
              </Typography>
           )}
        </Box>

        {/* Progress Bar Track */}
        <Box sx={{ height: 16, backgroundColor: COLORS.slate800, borderRadius: 8, width: '100%', position: 'relative', overflow: 'hidden' }}>
          <Box 
            sx={{ 
              height: '100%', 
              width: `${progressPercent}%`, 
              background: `linear-gradient(90deg, ${COLORS.amber600}, ${COLORS.amber500})`, 
              borderRadius: 8,
              transition: 'width 1s ease-in-out',
              boxShadow: '0 0 10px rgba(245, 158, 11, 0.5)'
            }} 
          />
        </Box>

        {/* Labels under bar */}
        <Stack direction="row" justifyContent="space-between" mt={1}>
          <Typography variant="caption" sx={{ color: COLORS.white, fontWeight: 'bold' }}>
            {formatMoney(currentDeposit)}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.slate400 }}>
            {nextRank ? `${t("vipnew.goal")}: ${formatMoney(nextRequirement)}` : 'MAX'}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default VipProgress;